import {useEffect, useState} from 'react'
import {Form} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {formatInches, parseHeightToInches} from '../helpers/bodyMeasures'

interface Props {
  /** Pulgadas totales, tal como se guardan en Member_policy.height. */
  value: string | number | null | undefined
  /** Recibe las pulgadas totales ya normalizadas, o '' si se vació el campo. */
  onChange: (totalInches: string) => void
  id?: string
  name?: string
  disabled?: boolean
}

/**
 * Estatura en formato americano.
 *
 * Mientras se escribe se respeta lo tipeado; al salir del campo se normaliza a
 * `5' 10"` y hacia el formulario viaja el entero de pulgadas totales. Así el agente
 * escribe como habla («cinco diez») y la base guarda un número comparable.
 */
export const HeightInput: React.FC<Props> = ({value, onChange, id, name, disabled}) => {
  const intl = useIntl()
  const [text, setText] = useState(() => formatInches(value))
  const [touched, setTouched] = useState(false)

  // Se resincroniza cuando el valor cambia desde afuera (carga de la póliza),
  // pero no mientras el usuario está escribiendo.
  useEffect(() => {
    if (!touched) setText(formatInches(value))
  }, [value, touched])

  const commit = () => {
    setTouched(false)
    const inches = parseHeightToInches(text)
    if (inches === null) {
      // No se pudo interpretar: se limpia en vez de guardar algo inválido.
      setText(text.trim() === '' ? '' : formatInches(value))
      if (text.trim() === '') onChange('')
      return
    }
    setText(formatInches(inches))
    onChange(String(inches))
  }

  const parsed = parseHeightToInches(text)
  const isInvalid = touched && text.trim() !== '' && parsed === null

  return (
    <>
      <Form.Control
        id={id}
        name={name}
        disabled={disabled}
        value={text}
        isInvalid={isInvalid}
        placeholder={intl.formatMessage({
          id: 'policies.detail.form.member.heightPlaceholder',
          defaultMessage: `e.g. 5'10"`,
        })}
        onChange={(e) => {
          setTouched(true)
          setText(e.target.value)
        }}
        onBlur={commit}
      />
      {isInvalid && (
        <Form.Text className='text-danger'>
          {intl.formatMessage({
            id: 'policies.detail.form.member.heightInvalid',
            defaultMessage: `Use feet and inches, e.g. 5'10"`,
          })}
        </Form.Text>
      )}
    </>
  )
}
